/**
 * O cofre de uma implantação, pelo console interno — a tela 8 da spec.
 *
 * O que ela mostra é o **inventário**, nunca o conteúdo: rótulo, de onde a credencial veio, quem
 * a entregou, quando, se foi rotacionada, até quando vale, sob que chave está cifrada e quantas
 * vezes já foi vista. O valor só aparece por um ato explícito, com motivo escrito, e esse ato
 * vira linha em `segredo_revelacao` antes de a senha chegar ao navegador.
 *
 * Três decisões desta tela:
 *
 * · **A custódia vem primeiro, antes da lista.** Ela é a diferença entre "a credencial está
 *   protegida por um serviço gerenciado" e "está protegida por um arquivo no host". Quem abre
 *   esta tela para revelar uma senha precisa saber disso antes de revelar, não depois.
 *
 * · **Esta tela não guarda e não rotaciona**, e a ausência é a matriz da spec §4, não
 *   esquecimento: `infra:responder` é do admin do cliente e do TI — de quem TEM a credencial.
 *   Botões que sempre devolveriam 403 ensinariam quem opera a ignorar recusa; a explicação
 *   escrita ensina onde a operação acontece. **Descartar, sim**: a retenção é `credencial:revelar`
 *   e é ato de quem opera o cofre, não de quem entregou a senha. Enquanto ela também era
 *   `infra:responder`, o ato que o ADR 0006 cobra não tinha executor possível de um lado e era
 *   destruição unilateral do outro.
 *
 * · **O descartado continua na lista, marcado.** Sumir com ele esconderia exatamente a
 *   credencial que existiu, foi usada e foi apagada — a linha que uma investigação procura.
 */

import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { Aviso, Cartao, Chip } from "../componentes/ui";
import { CartaoDaCustodia, DescarteDoSegredo, RevelacaoDoSegredo } from "../componentes/cofre";
import { ErroDaApi } from "../nucleo/api";
import { useTitulo } from "../nucleo/titulo";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { formatarCnpj, useClienteDaCarteira } from "../nucleo/implantacoes";
import { TRILHA_DE_INFRAESTRUTURA, caminhoDaRevisao } from "../nucleo/kickoff";
import {
  CHAVE_RAIZ_DO_COFRE,
  PERMISSAO_DE_REVELAR,
  ROTULO_DA_CUSTODIA_DO_SEGREDO,
  formatarInstante,
  ultimaGravacao,
  useCofreDaImplantacao,
  type CofreDaImplantacao as Cofre,
  type SegredoDoCofre,
} from "../nucleo/cofre";

export function CofreDaImplantacao() {
  const { id } = useParams();
  const implantacaoId = id ?? "";
  useTitulo("Cofre de credenciais");
  const consulta = useCofreDaImplantacao(implantacaoId);
  const cliente = useClienteDaCarteira(implantacaoId);
  const nome = cliente?.organizacao ?? "Cofre de credenciais";

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroDaApi
        ? consulta.error.message
        : "Não foi possível carregar o cofre agora. Tente de novo em instantes.";
    return (
      <>
        <h1 className="titulo-pagina">Cofre de credenciais</h1>
        <Aviso tom="erro" data-testid="falha-do-cofre">
          {mensagem}
        </Aviso>
        <p className="nota-rodape mt-4">
          <Link to="/admin/implantacoes">Voltar à carteira</Link>
        </p>
      </>
    );
  }

  return (
    <>
      <div className="cabecalho-da-trilha">
        <div>
          <h1 className="titulo-pagina">{nome}</h1>
          <p className="lead mb-2">
            Credenciais de acesso guardadas no cofre do portal.
            {cliente ? (
              <>
                {" "}
                <span className="numero-tabular">{formatarCnpj(cliente.cnpj)}</span>
              </>
            ) : null}
          </p>
        </div>
        {consulta.data ? (
          <Chip
            estado={consulta.data.sob_custodia_provisoria > 0 ? "atencao" : "neutro"}
            data-testid="placar-do-cofre"
          >
            {consulta.data.vivos} guardada{consulta.data.vivos === 1 ? "" : "s"}
          </Chip>
        ) : null}
      </div>

      <CartaoDaCustodia className="mb-5" />

      {consulta.isPending ? (
        <p className="lead" role="status">
          Carregando as credenciais…
        </p>
      ) : (
        <Credenciais cofre={consulta.data} implantacaoId={implantacaoId} />
      )}

      <p className="nota-rodape mt-4">
        <Link to="/admin/implantacoes">Voltar à carteira</Link> ·{" "}
        <Link to={caminhoDaRevisao(implantacaoId)}>Ver as respostas do kickoff</Link>
      </p>
    </>
  );
}

function Credenciais({
  cofre,
  implantacaoId,
}: {
  cofre: Cofre;
  implantacaoId: string;
}) {
  const sessao = useSessao();
  const cliente = useQueryClient();
  const podeRevelar = podeNaSessao(sessao.data, PERMISSAO_DE_REVELAR);

  /* Depois de revelar, a listagem é relida: o contador de revelações sobe NA FRENTE de quem
     acabou de revelar, e a data da última passa a ser agora. É o número que transforma o cofre
     em prestação de contas — deixá-lo parado até o próximo F5 ensinaria que revelar não custa
     nada. */
  function releuOCofre() {
    void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_COFRE });
  }

  if (cofre.segredos.length === 0) {
    return (
      <Cartao titulo="Nenhuma credencial guardada" data-testid="cofre-vazio">
        <Aviso tom="marca" data-testid="cofre-vazio">
          <b>Este cliente ainda não entregou nenhuma credencial pelo portal.</b> Elas chegam pelo
          questionário de infraestrutura — acesso ao servidor, banco do ERP e SMTP.
        </Aviso>
        <p className="cartao__texto mt-4">
          Se alguma credencial deste cliente já circulou por WhatsApp ou e-mail, peça ao cliente
          que a troque: o cofre não recolhe o que já saiu.
        </p>
      </Cartao>
    );
  }

  return (
    <>
      {!podeRevelar ? (
        <Aviso tom="marca" className="mb-4" data-testid="sem-permissao-de-revelar">
          <b>Você vê a lista, não os valores.</b> Só a equipe de infraestrutura pode revelar uma
          credencial; se você precisa de um destes acessos, peça a ela — a revelação fica
          registrada.
        </Aviso>
      ) : null}

      <div className="grade-do-cofre" data-testid="lista-de-segredos">
        {cofre.segredos.map((segredo) => (
          <CartaoDoSegredo
            key={segredo.id}
            segredo={segredo}
            podeRevelar={podeRevelar}
            implantacaoId={implantacaoId}
            aoRevelar={releuOCofre}
          />
        ))}
      </div>

      <Cartao className="mt-5" titulo="Como o cofre funciona">
        <p className="cartao__texto mb-3">
          Quem guarda ou troca uma credencial é o cliente, no questionário de infraestrutura. Aqui
          a equipe consulta, revela quando precisa usar e <b>descarta quando não precisa mais</b>.
        </p>
        <p className="cartao__texto">
          Cada revelação fica registrada com quem viu, quando e por quê. A contagem e a data da
          última aparecem em cada credencial.
        </p>
      </Cartao>
    </>
  );
}

function CartaoDoSegredo({
  segredo,
  podeRevelar,
  implantacaoId,
  aoRevelar,
}: {
  segredo: SegredoDoCofre;
  podeRevelar: boolean;
  implantacaoId: string;
  aoRevelar: () => void;
}) {
  const gravacao = ultimaGravacao(segredo);
  const criacao = formatarInstante(segredo.criado_em);
  const expira = formatarInstante(segredo.expira_em);
  const descarte = formatarInstante(segredo.descartado_em);
  const ultimaRevelacao = formatarInstante(segredo.ultima_revelacao_em);
  /* Descartado e vencido não são revelados pelo servidor — ele responde 409 com a explicação.
     A tela deixa de oferecer para não transformar a recusa esperada em erro na cara de quem
     opera; a recusa continua sendo dele, não daqui. */
  const revelavel = podeRevelar && !segredo.descartado && !segredo.expirado;

  return (
    <Cartao
      className="cartao-do-segredo"
      titulo={segredo.rotulo}
      /* Os três dados que a lista existe para carregar, como atributo e não só como texto: a
         custódia sob a qual a linha está cifrada, quantas vezes ela já foi vista e se ela foi
         descartada. O descartado continua na lista, marcado — sumir com ele esconderia
         exatamente a credencial que existiu, foi usada e foi apagada. */
      data-custodia={segredo.custodia}
      data-revelacoes={String(segredo.revelacoes)}
      data-descartado={segredo.descartado ? "sim" : "nao"}
      acessorio={
        <Chip
          estado={
            segredo.descartado
              ? "neutro"
              : segredo.expirado
                ? "erro"
                : segredo.sob_custodia_provisoria
                  ? "atencao"
                  : "ok"
          }
          data-testid="estado-do-segredo"
        >
          {segredo.descartado
            ? "descartada"
            : segredo.expirado
              ? "vencida"
              : segredo.sob_custodia_provisoria
                ? "chave provisória"
                : "guardada"}
        </Chip>
      }
    >
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 m-0 mb-3">
        <div>
          <dt className="campo__rotulo">Entregue por</dt>
          <dd className="m-0 text-sm" data-testid="quem-guardou">
            {segredo.criado_por?.nome ?? "—"}
            <span className="dado-secundario">{criacao ?? "data não registrada"}</span>
          </dd>
        </div>
        <div>
          <dt className="campo__rotulo">Rotação</dt>
          <dd className="m-0 text-sm" data-testid="rotacao-do-segredo">
            {gravacao.rotacionado ? (
              <>
                {gravacao.quem ?? "—"}
                <span className="dado-secundario">{gravacao.quando}</span>
              </>
            ) : (
              <>
                nunca rotacionada
                {/* Rotação não é higiene opcional: é o que limita a janela de uma credencial que
                    circulou por e-mail antes de o cofre existir. */}
                <span className="dado-secundario">
                  o valor guardado é o primeiro que o cliente enviou
                </span>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt className="campo__rotulo">Retenção</dt>
          <dd className="m-0 text-sm" data-testid="retencao-do-segredo">
            {descarte ? (
              <>
                descartada em {descarte}
                <span className="dado-secundario">
                  por {segredo.descartado_por?.nome ?? "—"}
                </span>
              </>
            ) : expira ? (
              <>
                {segredo.expirado ? "venceu em" : "vale até"} {expira}
              </>
            ) : (
              /* Nulo é "ninguém combinou prazo", nunca "vale para sempre por omissão" — a
                 política de retenção do ADR 0006 é um gate ainda aberto. Isso é estado interno
                 do projeto, não algo que quem opera o cofre faça: a tela diz só o fato. */
              <>sem prazo combinado</>
            )}
          </dd>
        </div>
        <div>
          <dt className="campo__rotulo">Revelações</dt>
          <dd className="m-0 text-sm" data-testid="revelacoes-do-segredo">
            <span className="numero-tabular">{segredo.revelacoes}</span>
            {segredo.revelacoes === 0 ? (
              <span className="dado-secundario">nunca foi vista por ninguém</span>
            ) : (
              <span className="dado-secundario">última em {ultimaRevelacao}</span>
            )}
          </dd>
        </div>
      </dl>

      <p className="cartao__texto" data-testid="procedencia-do-segredo">
        {segredo.origem_pergunta_id ? (
          <>
            Entregue pelo cliente no questionário de infraestrutura.{" "}
            {/* Cai na aba da infraestrutura e na própria pergunta (spec de 28/09, 6.1). */}
            <Link
              to={caminhoDaRevisao(implantacaoId, {
                trilha: TRILHA_DE_INFRAESTRUTURA,
                pergunta: segredo.origem_pergunta_id,
              })}
              data-testid="ver-resposta-do-segredo"
            >
              Ver a resposta no kickoff
            </Link>
          </>
        ) : (
          <>
            Registrada por fora do formulário, pelo console — não veio de uma resposta do cliente.
          </>
        )}
      </p>

      {/* A referência da chave mestra não aparece por segredo: ela identifica a chave do PORTAL,
          é a mesma string para todos os clientes, e esta listagem é aberta com `infra:ver` — que
          o admin e o TI do cliente têm. Quem precisa dela para decidir a migração de custódia a
          lê no cartão de custódia do console (`console:ver`). */}
      <p className="nota-rodape mt-2" data-testid="cifra-do-segredo">
        Guardada cifrada, com uma chave própria desta empresa. Proteção{" "}
        <b>{ROTULO_DA_CUSTODIA_DO_SEGREDO[segredo.custodia]}</b>.
      </p>

      {/* O terceiro aviso da MESMA tela saiu. O cartão do topo já diz que o cofre está sob a
          chave de emergência e quantas credenciais esperam re-embrulho; a linha logo acima já
          diz a custódia DESTA. Repetir o alerta em cada credencial não acrescentava informação —
          gastava a atenção de quem precisa agir, que é o efeito contrário do que um alerta serve
          para produzir. Ver o 4.4 dos ajustes da fase 1. */}

      <div className="cartao-do-segredo__acoes">
        {revelavel ? (
          <RevelacaoDoSegredo
            segredoId={segredo.id}
            rotulo={segredo.rotulo}
            aoRevelar={aoRevelar}
          />
        ) : podeRevelar ? (
          <p className="nota-rodape m-0" data-testid="nao-revelavel">
            {segredo.descartado
              ? "Descartada: não há mais material cifrado para revelar."
              : "Vencida: o prazo de retenção combinado passou e o cofre recusa revelá-la."}
          </p>
        ) : null}
        {/* A retenção anda com a revelação, e não com a gravação: quem pode ler toda credencial
            é quem responde por quanto tempo ela fica guardada. Só faz sentido no que ainda tem
            material — inclusive na vencida, que é justamente a que deveria sair do cofre. */}
        {podeRevelar && !segredo.descartado ? (
          <DescarteDoSegredo
            segredoId={segredo.id}
            rotulo={segredo.rotulo}
            aoDescartar={aoRevelar}
          />
        ) : null}
      </div>
    </Cartao>
  );
}
